/**
 * LandingScreen — home pública (`https://aetherlite.nilabs.co/`).
 *
 * Audiencia: visitantes sin auth (entrenadores potenciales) o coaches que
 * llegan al dominio raíz. Estilo editorial Nilabs, mobile-first, sin shell.
 *
 * Comportamiento:
 *   - Si el visitante hace login con Google, el AuthProvider lo lleva al shell
 *     y el dispatcher de App.tsx ya no monta esta pantalla.
 *   - Los CTAs "Empezar gratis" disparan el login Google embebido (lazy mount
 *     del botón oficial de GIS) sin redirigir a otra ruta.
 *
 * NO importa CatalogProvider ni TweaksPanel — bundle ligero para SEO y CWV.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Icon } from "../lib/Icon";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";
import { loginWithPassword } from "../api/auth";
import { ApiError } from "../api/client";

/** Reveal — wrapper de animación fadeUp al entrar en viewport. Una sola vez.
 *  Respeta prefers-reduced-motion vía el viewport once + duración corta. */
function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function LandingScreen() {
  const [staffLoginOpen, setStaffLoginOpen] = useState(false);
  return (
    <div className="al-land-root">
      <LandingNav onStaffLogin={() => setStaffLoginOpen(true)} />
      <Hero />
      <SocialProof />
      <Features />
      <DemoSection />
      <Compare />
      <Pricing />
      <FAQ />
      <LandingFooter />
      {staffLoginOpen && <StaffLoginModal onClose={() => setStaffLoginOpen(false)} />}
    </div>
  );
}

// ===========================================================================
// Nav superior
// ===========================================================================

function LandingNav({ onStaffLogin }: { onStaffLogin: () => void }) {
  return (
    <nav className="al-land-nav">
      <div className="al-land-nav-inner">
        <a href="/" className="al-land-brand" aria-label="AetherLite home">
          <span className="al-land-brand-mark">AL</span>
          <span className="al-land-brand-text">
            Aether<strong>Lite</strong>
          </span>
        </a>
        <div className="al-land-nav-links">
          <a href="#features" className="al-land-nav-link">Funcionalidades</a>
          <a href="#pricing" className="al-land-nav-link">Precios</a>
          <a href="#faq" className="al-land-nav-link">FAQ</a>
          {/* Acceso discreto para staff Nilabs — no es CTA principal */}
          <button
            type="button"
            className="al-land-nav-staff"
            onClick={onStaffLogin}
            title="Acceso para personal de Nilabs (admins de soporte)"
          >
            Staff
          </button>
          <button
            className="btn-primary btn-sm"
            onClick={() => scrollToHero()}
          >
            Empezar gratis
          </button>
        </div>
      </div>
    </nav>
  );
}

// ===========================================================================
// StaffLoginModal — login email+password para admins de soporte Nilabs
// ===========================================================================
// Los entrenadores siguen entrando por Google (CTA principal). Este modal es
// un flujo paralelo SOLO para admins creados desde el panel — gente que da
// soporte y no necesita una cuenta Google asociada al producto.

function StaffLoginModal({ onClose }: { onClose: () => void }) {
  const { refreshUser } = useAuth();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim() || !password) return;
    setBusy(true);
    setErr(null);
    try {
      await loginWithPassword(email.trim().toLowerCase(), password);
      // refreshUser dispara el AuthProvider para que vea el nuevo token y
      // cargue el `user`, así App.tsx ya monta el shell admin.
      await refreshUser?.();
      toast("Sesión iniciada");
      // El AuthProvider va a desmontar la landing automáticamente; igual
      // cerramos el modal por las dudas.
      onClose();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e instanceof Error ? e.message : String(e));
      setErr(msg || "No pudimos iniciar sesión");
      setBusy(false);
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="al-modal al-staff-login"
        role="dialog"
        aria-label="Acceso staff Nilabs"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Acceso staff Nilabs
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              Solo para administradores de soporte. Si sos entrenador, usá el botón de Google.
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar" disabled={busy}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <form onSubmit={submit} className="al-staff-login-body">
          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Email</span>
            <input
              type="email"
              className="al-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@nilabs.co"
              autoComplete="username"
              autoFocus
              disabled={busy}
              required
            />
          </label>
          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Contraseña</span>
            <input
              type="password"
              className="al-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={busy}
              required
              minLength={8}
            />
          </label>
          {err && <div className="al-staff-login-err">{err}</div>}
          <div className="al-staff-login-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy || !email.trim() || !password}
            >
              {busy ? "Entrando…" : "Entrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function scrollToHero() {
  const el = document.getElementById("al-land-hero");
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

function scrollToDemo() {
  const el = document.getElementById("al-land-demo");
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

// ===========================================================================
// Hero
// ===========================================================================

function Hero() {
  return (
    <section id="al-land-hero" className="al-land-hero">
      <div className="al-land-hero-bg" aria-hidden="true" />
      <div className="al-land-hero-inner">
        <div className="al-land-hero-copy">
          <div className="al-land-badge-ai">
            <Icon name="sparkle" size={13} /> AHORA CON GENERACIÓN POR IA
          </div>
          <h1 className="al-land-h1">
            Planes de entrenamiento con IA que tus clientes van a <span className="al-land-h1-accent">amar</span>
          </h1>
          <p className="al-land-lede">
            Diseñá, generá con IA y compartí planes profesionales en minutos.
            Tus clientes los siguen desde la app móvil y vos ves su progreso en
            tiempo real. Sin Word ni Canva.
          </p>
          <div className="al-land-cta-row">
            <GoogleLoginCta label="Empezar gratis" />
            <button className="btn-secondary btn-lg" onClick={scrollToDemo}>
              Ver demo
            </button>
          </div>
          <div className="al-land-hero-meta">
            <span><Icon name="check" size={14} /> Sin tarjeta de crédito</span>
            <span><Icon name="check" size={14} /> App móvil incluida</span>
          </div>
        </div>
        <div className="al-land-hero-visual">
          <FakePlanCard />
        </div>
      </div>
    </section>
  );
}

/**
 * Botón de login Google embebido. Reusa el mismo flujo que <LoginScreen>:
 *   - Espera a que `google.accounts.id` esté disponible.
 *   - Renderiza el botón oficial de Google Identity Services.
 *   - Al login exitoso, AuthProvider actualiza `user` y App.tsx desmonta esta
 *     pantalla automáticamente (re-render con shell).
 *
 * Mientras carga el script o el user clickea, mostramos un fallback estilizado.
 */
function GoogleLoginCta({ label }: { label: string }) {
  const { loginGoogle } = useAuth();
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(
    typeof window !== "undefined" && Boolean(window.google?.accounts?.id),
  );
  const [busy, setBusy] = useState(false);

  // Poll para detectar cuándo carga el SDK.
  useEffect(() => {
    if (ready) return;
    const t = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        setReady(true);
        window.clearInterval(t);
      }
    }, 200);
    return () => window.clearInterval(t);
  }, [ready]);

  // Render del botón oficial.
  useEffect(() => {
    if (!ready || !ref.current) return;
    const clientId = window.GOOGLE_CLIENT_ID || import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    window.google!.accounts.id.initialize({
      client_id: clientId,
      callback: async (resp: { credential: string }) => {
        try {
          setBusy(true);
          await loginGoogle(resp.credential);
        } catch (e) {
          toast(e instanceof Error ? e.message : "No pudimos iniciar sesión.");
          setBusy(false);
        }
      },
      auto_select: false,
      ux_mode: "popup",
    });
    ref.current.innerHTML = "";
    window.google!.accounts.id.renderButton(ref.current, {
      type: "standard",
      theme: "filled_blue",
      size: "large",
      text: "continue_with",
      shape: "pill",
      logo_alignment: "left",
      width: 240,
    });
  }, [ready, loginGoogle, toast]);

  return (
    <div className="al-land-google-cta">
      <div
        ref={ref}
        className="al-land-google-mount"
        style={{ opacity: busy ? 0.5 : 1, pointerEvents: busy ? "none" : "auto" }}
        aria-label={label}
      />
      {!ready && (
        <button className="btn-primary btn-lg" disabled>
          {label}
        </button>
      )}
    </div>
  );
}

/**
 * Fake plan card — mockup CSS-only. Imita la portada del plan que produce el
 * editor (cover section) con los colores de la marca. Sirve como "screenshot
 * vivo" en el hero sin depender de assets binarios.
 */
function FakePlanCard() {
  return (
    <div className="al-land-mock-cover">
      <div className="al-land-mock-deco" />
      <div className="al-land-mock-top">
        <div className="al-land-mock-mark">CN</div>
        <div className="al-land-mock-meta">
          <div>PROGRAMA</div>
          <strong>MAYO 2026</strong>
          <div>4 SEMANAS</div>
        </div>
      </div>
      <div className="al-land-mock-mid">
        <div className="al-land-mock-eyebrow">— PLAN DE ENTRENAMIENTO</div>
        <div className="al-land-mock-title">
          Hipertrofia <span>Avanzada</span>
        </div>
        <div className="al-land-mock-sub">Programación periodizada de 4 semanas, ajustada a tu objetivo.</div>
      </div>
      <div className="al-land-mock-foot">
        <div>
          <div className="al-land-mock-foot-lbl">CLIENTE</div>
          <div className="al-land-mock-foot-val">Andrés Pérez</div>
        </div>
        <div>
          <div className="al-land-mock-foot-lbl">ENTRENADOR</div>
          <div className="al-land-mock-foot-val">Coach Nilabs</div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Social proof
// ===========================================================================

function SocialProof() {
  return (
    <section className="al-land-proof">
      <div className="al-land-proof-text">+200 entrenadores en LATAM ya usan AetherLite</div>
      <div className="al-land-proof-row" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="al-land-proof-logo" />
        ))}
      </div>
    </section>
  );
}

// ===========================================================================
// Features (3 columnas)
// ===========================================================================

function Features() {
  return (
    <section id="features" className="al-land-features">
      <div className="al-land-section-head">
        <div className="al-land-eyebrow">QUÉ HACE</div>
        <h2 className="al-land-h2">Una herramienta pensada para entrenadores reales</h2>
        <p className="al-land-lede" style={{ maxWidth: 620 }}>
          Diseñá planes con estilo profesional, sin diseñadores ni fricciones técnicas.
        </p>
      </div>
      <div className="al-land-feat-grid">
        <Reveal delay={0}>
          <FeatCard
            icon="sparkle"
            title="Generación con IA"
            body="Pasás los datos del cliente y la IA arma rutina, dieta, cardio y suplementos en segundos. Vos revisás y ajustás. Usa solo ejercicios y alimentos del catálogo, nunca inventados."
          />
        </Reveal>
        <Reveal delay={0.08}>
          <FeatCard
            icon="hammer"
            title="Editor inline tipo Canva"
            body="Click sobre cualquier texto y editás. Cambiá portada, días de rutina, macros y notas sin abrir Word ni copiar formatos."
          />
        </Reveal>
        <Reveal delay={0.16}>
          <FeatCard
            icon="book"
            title="1.408 ejercicios con GIFs"
            body="Catálogo en español con animaciones, músculo trabajado y equipamiento. Insertás un ejercicio y aparece automáticamente con su GIF."
          />
        </Reveal>
        <Reveal delay={0.24}>
          <FeatCard
            icon="bell"
            title="App móvil para tu cliente"
            body="Tu cliente sigue su plan desde la app, marca entrenamientos y sube fotos de progreso. Vos recibís notificaciones y lo ves todo desde tu app de coach."
          />
        </Reveal>
        <Reveal delay={0.32}>
          <FeatCard
            icon="send"
            title="Comparte con un link"
            body="Generás un link único por cliente. Lo abre desde el celular y ve su plan responsive, con tu marca y tus colores."
          />
        </Reveal>
        <Reveal delay={0.4}>
          <FeatCard
            icon="star"
            title="Tu marca en cada plan"
            body="Logo, colores, tipografías y portada propios. El cliente ve un plan profesional con TU identidad, no la nuestra."
          />
        </Reveal>
      </div>
    </section>
  );
}

function FeatCard({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <article className="al-land-feat">
      <div className="al-land-feat-icon">
        <Icon name={icon} size={22} />
      </div>
      <h3 className="al-land-feat-title">{title}</h3>
      <p className="al-land-feat-body">{body}</p>
    </article>
  );
}

// ===========================================================================
// Demo section (3 mockups)
// ===========================================================================

function DemoSection() {
  return (
    <section id="al-land-demo" className="al-land-demo">
      <div className="al-land-section-head">
        <div className="al-land-eyebrow">CÓMO SE VE</div>
        <h2 className="al-land-h2">Profesional en cada paso</h2>
      </div>
      <div className="al-land-demo-grid">
        <DemoCol
          label="Portada"
          body="Tu marca, tus colores, tu firma. La portada vende tu trabajo antes de que el cliente vea el contenido."
        >
          <FakePlanCard />
        </DemoCol>
        <DemoCol
          label="Rutina"
          body="Días, ejercicios, series, reps, peso, RIR y notas — todo editable inline. Con GIFs animados embebidos."
        >
          <MockRoutine />
        </DemoCol>
        <DemoCol
          label="Vista del cliente"
          body="Tu cliente abre el link y ve un plan limpio en su móvil. Sin descargas, sin app, sin login."
        >
          <MockMobile />
        </DemoCol>
      </div>
    </section>
  );
}

function DemoCol({
  label,
  body,
  children,
}: {
  label: string;
  body: string;
  children: React.ReactNode;
}) {
  return (
    <div className="al-land-demo-col">
      <div className="al-land-demo-stage">{children}</div>
      <div className="al-land-demo-tag">{label}</div>
      <p className="al-land-demo-body">{body}</p>
    </div>
  );
}

function MockRoutine() {
  return (
    <div className="al-land-mock-routine">
      <div className="al-land-mock-day-head">
        <strong>Lunes</strong>
        <span>Pecho · Tríceps</span>
      </div>
      {[
        { n: "Press banca con barra", s: "4×8" },
        { n: "Press inclinado mancuernas", s: "3×10" },
        { n: "Aperturas en polea", s: "3×12" },
        { n: "Fondos en paralelas", s: "3×10" },
      ].map((ex, i) => (
        <div key={i} className="al-land-mock-ex">
          <div className="al-land-mock-ex-thumb" />
          <div className="al-land-mock-ex-body">
            <div className="al-land-mock-ex-name">{ex.n}</div>
            <div className="al-land-mock-ex-meta">{ex.s} · 90s</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MockMobile() {
  return (
    <div className="al-land-mock-phone">
      <div className="al-land-mock-phone-notch" />
      <div className="al-land-mock-phone-screen">
        <div className="al-land-mock-phone-header">
          <div className="al-land-mock-phone-mark">CN</div>
          <div>
            <div className="al-land-mock-phone-coach">Coach Nilabs</div>
            <div className="al-land-mock-phone-tag">Entrenador personal</div>
          </div>
        </div>
        <div className="al-land-mock-phone-greet">
          <div className="al-land-mock-phone-eyebrow">TU PLAN</div>
          <div className="al-land-mock-phone-title">Hola Andrés 👋</div>
          <div className="al-land-mock-phone-sub">Hipertrofia Avanzada</div>
        </div>
        <div className="al-land-mock-phone-card" />
        <div className="al-land-mock-phone-card short" />
      </div>
    </div>
  );
}

// ===========================================================================
// Pricing
// ===========================================================================

function Pricing() {
  return (
    <section id="pricing" className="al-land-pricing">
      <div className="al-land-section-head">
        <div className="al-land-eyebrow">PLANES</div>
        <h2 className="al-land-h2">Precios honestos. Cancelás cuando quieras.</h2>
        <p className="al-land-lede" style={{ maxWidth: 580 }}>
          Empezá gratis. Cuando tu negocio crezca, sumás clientes pagando un mes a la vez.
        </p>
      </div>
      <div className="al-land-pricing-grid al-land-pricing-grid-4">
        <PriceCard
          name="Free"
          price="$0"
          period="para siempre"
          highlights={[
            "3 clientes activos",
            "1.408 ejercicios con GIFs",
            "App móvil para tus clientes",
            "15 plantillas premium",
            "Marca de agua AetherLite",
          ]}
          cta="Empezar gratis"
        />
        <PriceCard
          name="Solo"
          price="$49.000"
          period="COP / mes"
          highlights={[
            "15 clientes activos",
            "Sin marca de agua",
            "App móvil para tus clientes",
            "30 generaciones de plan con IA / mes",
            "Tu marca + colores propios",
          ]}
          cta="Probar Solo"
        />
        <PriceCard
          name="Studio"
          price="$89.000"
          period="COP / mes"
          featured
          highlights={[
            "50 clientes activos",
            "90 generaciones de plan con IA / mes",
            "Editor avanzado tipo Canva",
            "Estadísticas de visualización",
            "Soporte prioritario",
          ]}
          cta="Probar Studio"
        />
        <PriceCard
          name="Pro"
          price="$149.000"
          period="COP / mes"
          highlights={[
            "Hasta 120 clientes activos",
            "200 generaciones de plan con IA / mes",
            "Plantillas exclusivas",
            "Onboarding personalizado",
            "Soporte por WhatsApp",
          ]}
          cta="Probar Pro"
        />
        <PriceCard
          name="Gimnasio"
          price="A medida"
          period="varios entrenadores"
          highlights={[
            "Para gimnasios y centros de acondicionamiento",
            "Varios entrenadores en una sola cuenta",
            "Clientes a escala + IA ampliada",
            "Facturación única",
            "Onboarding dedicado",
          ]}
          cta="Contactanos"
          href="mailto:aetherlite@nilabs.co?subject=Plan%20Gimnasio%20AetherLite"
        />
      </div>
    </section>
  );
}

function PriceCard({
  name,
  price,
  period,
  highlights,
  cta,
  featured,
  href,
}: {
  name: string;
  price: string;
  period: string;
  highlights: string[];
  cta: string;
  featured?: boolean;
  /** Si está, el CTA es un link (ej. mailto para "Contactanos") en vez de
   *  scrollear al login de Google. Usado por el plan Gimnasio (venta asistida). */
  href?: string;
}) {
  const btnClass = featured ? "btn-primary btn-lg" : "btn-secondary btn-lg";
  return (
    <div className={"al-land-price " + (featured ? "al-land-price-featured" : "")}>
      {featured && <div className="al-land-price-badge">Más elegido</div>}
      <div className="al-land-price-name">{name}</div>
      <div className="al-land-price-amount">
        {price}
        <span className="al-land-price-period"> · {period}</span>
      </div>
      <ul className="al-land-price-list">
        {highlights.map((h) => (
          <li key={h}>
            <Icon name="check" size={14} /> {h}
          </li>
        ))}
      </ul>
      {href ? (
        <a className={btnClass} href={href} style={{ textAlign: "center" }}>{cta}</a>
      ) : (
        <button className={btnClass} onClick={scrollToHero}>{cta}</button>
      )}
    </div>
  );
}

// ===========================================================================
// Comparativa vs competencia
// ===========================================================================

/** Filas de la tabla comparativa. `us` true = ventaja nuestra destacada. */
const COMPARE_ROWS: { feature: string; aetherlite: string; others: string }[] = [
  { feature: "Generación de planes con IA", aetherlite: "Sí, incluida", others: "Raro / extra" },
  { feature: "App móvil para el cliente", aetherlite: "Sí, gratis", others: "Según plan" },
  { feature: "Editor visual tipo Canva", aetherlite: "Sí", others: "No" },
  { feature: "Catálogo 1.408 ejercicios con GIFs", aetherlite: "Sí", others: "Varía" },
  { feature: "Español neutro LATAM", aetherlite: "Sí", others: "Inglés / ES España" },
  { feature: "Precio para 50 clientes", aetherlite: "$89.000 COP", others: "$200k–450k COP" },
];

function Compare() {
  return (
    <section className="al-land-compare">
      <div className="al-land-compare-inner">
        <div className="al-land-section-head">
          <div className="al-land-eyebrow">POR QUÉ AETHERLITE</div>
          <h2 className="al-land-h2">Más completo, en tu idioma, a precio LATAM</h2>
          <p className="al-land-lede" style={{ maxWidth: 620 }}>
            Comparado con las plataformas internacionales de coaching, AetherLite
            te da IA y app móvil sin los precios en dólares.
          </p>
        </div>
        <table className="al-land-compare-table">
          <thead>
            <tr>
              <th>Funcionalidad</th>
              <th className="is-us">AetherLite</th>
              <th>Otras plataformas</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((row) => (
              <tr key={row.feature}>
                <th scope="row" style={{ fontWeight: 600 }}>{row.feature}</th>
                <td className="is-us">{row.aetherlite}</td>
                <td>{row.others}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ===========================================================================
// FAQ
// ===========================================================================

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "¿Necesito tarjeta de crédito para empezar?",
    a: "No. Empezás 100% gratis con tu cuenta de Google, hasta 3 clientes y con la app móvil incluida. Subís de plan solo cuando lo necesites.",
  },
  {
    q: "¿Qué hace la generación con IA?",
    a: "En segundos, la IA arma un plan completo (rutina + dieta + cardio + suplementos) a partir de los datos del cliente: edad, peso, objetivo, equipamiento y lesiones. Vos lo revisás y ajustás antes de enviarlo. Usás ejercicios y alimentos de nuestro catálogo, nunca inventados.",
  },
  {
    q: "¿Tienen app móvil?",
    a: "Sí. Tu cliente descarga la app de AetherLite, ve su plan, marca sus entrenamientos completados y registra su progreso (peso, medidas, fotos). Vos tenés una app de coach para monitorear todo desde el celular y recibir notificaciones cuando entrenan.",
  },
  {
    q: "¿Cómo entra mi cliente a la app?",
    a: "Le mandás un código desde la app o la web (por WhatsApp). El cliente lo pega en la app y entra sin contraseña. También puede ver su plan por link web si prefiere no instalar nada.",
  },
  {
    q: "¿Cómo cobran los planes?",
    a: "Con MercadoPago, cobro recurrente automático. Cancelás cuando quieras, sin penalidad. Si te das de baja, tus planes siguen accesibles en lectura por 30 días.",
  },
  {
    q: "¿En qué se diferencia de AetherCoach?",
    a: "AetherLite es la versión de entrada: editor de planes, IA, app móvil y tracking. AetherCoach es la plataforma premium con análisis de fotos por IA, antropometría profesional, gamificación, comunidad, marketplace y chat en tiempo real. Cuando tu negocio crece, das el salto.",
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="al-land-faq">
      <div className="al-land-section-head">
        <div className="al-land-eyebrow">PREGUNTAS</div>
        <h2 className="al-land-h2">Todo lo que querés saber antes de empezar</h2>
      </div>
      <div className="al-land-faq-list">
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className={"al-land-faq-item " + (isOpen ? "open" : "")}>
              <button
                className="al-land-faq-q"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
              >
                <span>{item.q}</span>
                <Icon name={isOpen ? "x" : "plus"} size={16} />
              </button>
              {isOpen && <div className="al-land-faq-a">{item.a}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ===========================================================================
// Footer
// ===========================================================================

function LandingFooter() {
  return (
    <footer className="al-land-footer">
      <div className="al-land-footer-grid">
        <div>
          <div className="al-land-brand" style={{ marginBottom: 12 }}>
            <span className="al-land-brand-mark">AL</span>
            <span className="al-land-brand-text">
              Aether<strong>Lite</strong>
            </span>
          </div>
          <p className="al-land-footer-blurb">
            Editor web para entrenadores personales. Una iniciativa de Nilabs.
          </p>
        </div>
        <div>
          <div className="al-land-footer-title">Producto</div>
          <a href="#pricing" className="al-land-footer-link">Precios</a>
          <a href="/#/terms" className="al-land-footer-link">Términos</a>
          <a href="/#/privacy" className="al-land-footer-link">Privacidad</a>
        </div>
        <div>
          <div className="al-land-footer-title">Recursos</div>
          <span className="al-land-footer-link disabled">Blog · próximamente</span>
          <a
            href="https://aethercoach.nilabs.co/health"
            target="_blank"
            rel="noopener noreferrer"
            className="al-land-footer-link"
          >
            Status
          </a>
        </div>
        <div>
          <div className="al-land-footer-title">Contacto</div>
          <a href="mailto:nilton.alfonso@nilabs.co" className="al-land-footer-link">
            nilton.alfonso@nilabs.co
          </a>
          <a
            href="https://nilabs.co"
            target="_blank"
            rel="noopener noreferrer"
            className="al-land-footer-link"
          >
            nilabs.co
          </a>
        </div>
      </div>
      <div className="al-land-footer-bottom">
        © 2026 Nilabs · AetherLite es parte del ecosistema Nilabs. Otros productos:
        AetherCoach (próximamente público), Panini.
      </div>
    </footer>
  );
}
